import { Intersection, Material, Mesh, Object3D, Texture } from 'three'

/**
 * Converts HSL to hex
 *
 * @param h Hue in degrees (0-360)
 * @param s Saturation in percentage (0-100)
 * @param l Lightness in percentage (0-100)
 */
const hslToHex = (h: number, s: number, l: number): string => {
  const sNorm = s / 100
  const lNorm = l / 100

  const k = (n: number) => (n + h / 30) % 12
  const a = sNorm * Math.min(lNorm, 1 - lNorm)
  const f = (n: number) => Math.round(255 * (lNorm - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))))

  return `#${[f(0), f(8), f(4)].map((x) => x.toString(16).padStart(2, '0')).join('')}`
}

/**
 * Generates a random vibrant HSL color.
 */
const getVibrantHslColor = () => {
  // Use HSL and convert to hex
  const hue = Math.floor(Math.random() * 360) // Full range
  const saturation = 70 + Math.random() * 20 // 70–90% saturation
  const lightness = 50 + Math.random() * 10 // 50–60% lightness

  return { hue, saturation, lightness }
}

/**
 * Generates a random vibrant hex color.
 *
 * @returns A random vibrant hex color.
 */
export const getVibrantHexColor = (): string => {
  const { hue, saturation, lightness } = getVibrantHslColor()
  return hslToHex(hue, saturation, lightness)
}

/**
 * Returns 'black' or 'white' based on which has better contrast with the given hex color.
 * @param hex - Hex color string (e.g., '#ffffff' or '#abc')
 */
export function getContrastingOutlineColor(hex: string): 'black' | 'white' {
  const normalizedHex = hex.length === 4 ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}` : hex

  const r = parseInt(normalizedHex.slice(1, 3), 16)
  const g = parseInt(normalizedHex.slice(3, 5), 16)
  const b = parseInt(normalizedHex.slice(5, 7), 16)

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

  return luminance > 0.5 ? 'black' : 'white'
}

/**
 * One reused 1x1 scratch canvas for texture sampling. A full-size `ImageData` of a 4K
 * texture atlas would cost tens of megabytes per sample, so only the single pixel under
 * the pointer is ever copied.
 */
let textureSamplingContext: CanvasRenderingContext2D | null | undefined

const getTextureSamplingContext = (): CanvasRenderingContext2D | null => {
  if (textureSamplingContext === undefined) {
    const canvas = document.createElement('canvas')
    canvas.width = 1
    canvas.height = 1
    textureSamplingContext = canvas.getContext('2d', { willReadFrequently: true })
  }

  return textureSamplingContext
}

const toHex = (channel: number): string => channel.toString(16).padStart(2, '0')

/** What a mesh texture's `image` can be, and what `createImageBitmap` accepts. */
type TextureImage = HTMLImageElement | HTMLCanvasElement | ImageBitmap

/**
 * Decoded copies of mesh textures, keyed by the source image.
 *
 * Drawing from an `HTMLImageElement` re-decodes the whole JPEG on every call once the browser has
 * dropped its CPU-side copy — which it does after three.js uploads the texture to the GPU. At
 * pointer rate that allocates hundreds of MB/s and the tab stalls in GC for about a second at a
 * time. Decoding once up front and blitting from the result removes both.
 */
let decodedTextures = new WeakMap<TextureImage, ImageBitmap>()

/** Held so they can be closed on release; `decodedTextures` alone cannot be enumerated. */
const openTextureBitmaps = new Set<ImageBitmap>()

/** In flight, so a second request for the same texture does not start a second decode. */
const pendingTextureDecodes = new Set<TextureImage>()

/** Bumped on release so a decode that lands afterwards is discarded rather than cached. */
let textureDecodeGeneration = 0

const decodeTexture = (image: TextureImage) => {
  if (decodedTextures.has(image) || pendingTextureDecodes.has(image)) return

  pendingTextureDecodes.add(image)
  const generation = textureDecodeGeneration

  void createImageBitmap(image)
    .then((bitmap) => {
      pendingTextureDecodes.delete(image)

      if (generation !== textureDecodeGeneration) {
        bitmap.close()
        return
      }

      decodedTextures.set(image, bitmap)
      openTextureBitmaps.add(bitmap)
    })
    .catch(() => {
      pendingTextureDecodes.delete(image)
    })
}

/**
 * The texture image on one material, if it has a usable one.
 *
 * @param material Material to read.
 */
const getTextureImage = (material: Material | undefined): TextureImage | undefined => {
  if (!material || !('map' in material)) return undefined

  const image = (material.map as Texture | null)?.image as TextureImage | undefined
  return image?.width && image.height ? image : undefined
}

/**
 * Every material on a mesh, as a list.
 *
 * A tile whose texture pack holds more than one image has *all* of its materials assigned to
 * each of its meshes as an array, with the geometry's groups deciding which one draws which
 * face. So a mesh routinely carries several materials and cannot be read as just one.
 *
 * @param object Object to read.
 */
const getMaterials = (object: Object3D): Material[] => {
  const material = (object as Mesh).material as Material | Material[] | undefined
  if (!material) return []

  return Array.isArray(material) ? material : [material]
}

/**
 * Decode the textures under an object ahead of time, so the first sample does not pay for it.
 *
 * Covers every material on each mesh, because which one a later sample lands on depends on the
 * face it hits.
 *
 * @param object Object whose subtree's textures should be decoded.
 */
export const prewarmTextureSampling = (object: Object3D) => {
  object.traverse((child) => {
    getMaterials(child).forEach((material) => {
      const image = getTextureImage(material)
      if (image) decodeTexture(image)
    })
  })
}

/**
 * Drop every decoded texture. Call when sampling is done — the bitmaps are full-size and hold
 * memory the garbage collector will not reclaim on its own.
 */
export const releaseTextureSampling = () => {
  textureDecodeGeneration += 1
  pendingTextureDecodes.clear()
  openTextureBitmaps.forEach((bitmap) => bitmap.close())
  openTextureBitmaps.clear()
  decodedTextures = new WeakMap()
}

/**
 * Reads the colour of a mesh's own texture at the point a raycast hit it, as a hex string.
 *
 * This samples the *texture*, not the rendered framebuffer, because the framebuffer carries
 * lighting and any translucent overlay drawn on top of the mesh.
 *
 * Reads only from an already-decoded texture. Until one is ready this returns undefined rather
 * than falling back to the raw image, because that fallback is precisely what makes the tab
 * stall — see `decodedTextures`. Use `prewarmTextureSampling` so the decode is done in advance.
 *
 * @param intersection Raycast intersection with a textured mesh (needs `uv`).
 * @returns Hex colour (e.g. `#a1b2c3`), or undefined when there is nothing readable there yet.
 */
export const sampleTextureColorAtIntersection = (intersection: Intersection<Object3D>): string | undefined => {
  const { uv, object, face } = intersection
  if (!uv) return undefined

  // A face drawn from a multi-material mesh names its own material through its geometry group,
  // which three.js reports on the hit face. Falling back to the first material keeps a
  // single-material mesh — where there is no group and no index — working.
  const material = getMaterials(object)[face?.materialIndex ?? 0]
  if (!material || !('map' in material)) return undefined

  const map = material.map as Texture | null
  const image = getTextureImage(material)
  if (!map || !image) return undefined

  const decoded = decodedTextures.get(image)
  if (!decoded) {
    // Not warmed yet (or a tile the prewarm missed) — start it and show nothing this frame.
    decodeTexture(image)
    return undefined
  }

  const context = getTextureSamplingContext()
  if (!context) return undefined

  // Let three.js resolve offset/repeat/wrapping/flipY rather than reimplementing it here.
  if (map.matrixAutoUpdate) map.updateMatrix()
  const textureUv = map.transformUv(uv.clone())

  const x = Math.min(Math.max(Math.floor(textureUv.x * decoded.width), 0), decoded.width - 1)
  const y = Math.min(Math.max(Math.floor(textureUv.y * decoded.height), 0), decoded.height - 1)

  try {
    context.clearRect(0, 0, 1, 1)
    context.drawImage(decoded, x, y, 1, 1, 0, 0, 1, 1)
    const [r, g, b, a] = context.getImageData(0, 0, 1, 1).data
    if (!a) return undefined

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`
  } catch {
    // Textures fetched straight from S3 (the MTLLoader path) taint the canvas and make
    // `getImageData` throw. A colour preview must never break what it previews, so give up quietly.
    return undefined
  }
}
